import type { Viewport } from '../interfaces/board-object.interface.js';

export interface ZoomOptions {
  /** How aggressive a single wheel step is. Applied multiplicatively. */
  scaleBy: number;
  /** Hard lower bound for the viewport scale. */
  minScale: number;
  /** Hard upper bound for the viewport scale. */
  maxScale: number;
}

export const DEFAULT_ZOOM: ZoomOptions = {
  scaleBy: 1.05,
  minScale: 0.1,
  maxScale: 10,
};

/**
 * Return a new viewport that has been zoomed *around the pointer*:
 * the world coordinate directly under the pointer stays visually
 * anchored to the same screen pixel. Positive `direction` zooms in,
 * negative zooms out.
 *
 * Pure function — no DOM, no Konva — so the math is fully unit-testable.
 */
export function zoomAtPointer(
  viewport: Viewport,
  pointer: { x: number; y: number },
  direction: number,
  options: ZoomOptions = DEFAULT_ZOOM,
): Viewport {
  const factor = direction > 0 ? options.scaleBy : 1 / options.scaleBy;
  return zoomToScaleAt(viewport, pointer, viewport.scale * factor, options);
}

/**
 * Wie {@link zoomAtPointer}, aber auf eine FESTE Stufe statt schrittweise: Der
 * Weltpunkt unter `anchor` (ein Bildschirm-Punkt) bleibt an genau diesem Pixel
 * kleben, waehrend die Zoomstufe auf `newScale` wechselt.
 *
 * Anke, 2026-07-16 — dafuer gedacht, den Bildmittelpunkt als Anker zu nehmen:
 * Beim Wechsel auf 100 % soll sich die Stufe aendern, nicht die Stelle, auf die
 * man schaut. Ohne Anker (nur `scale` setzen und x/y stehen lassen) bleibt in
 * Wahrheit die linke obere ECKE stehen, und das Bild wandert.
 *
 * Reine Funktion — kein DOM, kein Konva, damit die Rechnung ohne Browser
 * pruefbar bleibt.
 */
export function zoomToScaleAt(
  viewport: Viewport,
  anchor: { x: number; y: number },
  newScale: number,
  options: ZoomOptions = DEFAULT_ZOOM,
): Viewport {
  const scale = clamp(newScale, options.minScale, options.maxScale);
  if (scale === viewport.scale) return viewport;

  const world = screenToWorld(viewport, anchor);

  return {
    x: anchor.x - world.x * scale,
    y: anchor.y - world.y * scale,
    scale,
  };
}

/** Screen coordinate → world coordinate given a viewport. */
export function screenToWorld(viewport: Viewport, screen: { x: number; y: number }): { x: number; y: number } {
  return {
    x: (screen.x - viewport.x) / viewport.scale,
    y: (screen.y - viewport.y) / viewport.scale,
  };
}

/** World coordinate → screen coordinate given a viewport (inverse of `screenToWorld`). */
export function worldToScreen(viewport: Viewport, world: { x: number; y: number }): { x: number; y: number } {
  return {
    x: world.x * viewport.scale + viewport.x,
    y: world.y * viewport.scale + viewport.y,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
